// cSpell:ignore afgerekend afrekening Betaald Betalingen Deels Opgenomen Scenariolaan vertrek Wijzigen
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertyCostHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.hooks";
import * as propertyCostPromises from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.promises";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";
import { withLivePaidSplits } from "scenes/Settlements/components/CostsTable";
import { InvoiceViewModal } from "scenes/Settlements/components/InvoiceViewModal";
import { MissingDetail } from "scenes/Settlements/components/MissingDetail";
import { useCostDrawerSplit } from "scenes/Settlements/costDrawerSplit";
import { costsHaveOpenPayment } from "scenes/Settlements/costPayment.utils";
import {
  costSettledInfo,
  costSettlementStateOf,
  costSettlementStates,
} from "scenes/Settlements/costSettlementState.utils";
import { openPaymentMessage } from "scenes/Settlements/costsList.utils";
import {
  addressPartsOf,
  unitAddressLabel,
} from "scenes/Settlements/groupUnits.utils";
import {
  deleteRefusedMessage,
  useCanEditCosts,
  useCostPaymentStatusMaps,
  useGroupUnits,
  useSettlements,
  useSiblingInvoices,
} from "scenes/Settlements/hooks";
import { getMissingDetailState } from "scenes/Settlements/missingDetail.utils";
import { propertyCostToInvoice } from "scenes/Settlements/propertyCost.mapper";
import { CATEGORY_LABELS_NL, Invoice } from "scenes/Settlements/types";
import { StringParam } from "serialize-query-params";

interface IProps {
  propertyCostId: string;
  onDrawerClose?: () => void;
}

const PropertyCost: React.FC<IProps> = ({ propertyCostId, onDrawerClose }) => {
  const dispatch = useDispatch();
  const [, setEditParams] = useQueryParams({
    propertyCostEditId: StringParam,
    propertyCostId: StringParam,
  });
  const {
    detail: cost,
    isFetching,
    fetchError,
    refetch,
  } = propertyCostHooks.useDetail({
    id: propertyCostId,
    shouldRefetch: true,
  });

  const rawInvoice: Invoice | undefined = useMemo(
    () => (cost ? propertyCostToInvoice(cost as any) : undefined),
    [cost],
  );

  const propertyId = rawInvoice
    ? Number(rawInvoice.unitId ?? rawInvoice.groupId)
    : undefined;
  // Changing a cost needs all of the cost's own property: the owner of a
  // group record in a building with other owners only reads it.
  const canEdit = useCanEditCosts(propertyId);
  const { detail: property } = propertyHooks.useDetail({
    id: propertyId,
    shouldRefetch: !!propertyId,
  });

  // Overlay the LIVE payment status on the stored splits, so the drawer shows
  // "Betaald" as soon as the linked payment request is paid — same derivation
  // as the costs overview table.
  const statusInvoices = useMemo(
    () => (rawInvoice ? [rawInvoice] : []),
    [rawInvoice],
  );
  // A group cost's per-tenant payments live on the individual unit-properties
  // (per-tenant loop) or on the group itself (bulk) — fetch the request status
  // for the group AND its units, otherwise the split state never flips. The
  // owner of one unit cannot read the group, but a cost they read as their
  // own part is a group's (see isGroupCostDetail): their own units are fetched.
  const isGroupProperty =
    (property as any)?.typeId === EPropertyTypeId.Group ||
    !!rawInvoice?.viewerPart;
  // A group cost is managed on the group. Opened on one of its units (the
  // unit's list of group costs asked from its tenants) it is read-only.
  const propertyPage = useMatch("/properties/:propertyId/*");
  const pagePropertyId = propertyPage?.params.propertyId;
  // The cost of an archived property is only read until it is activated
  // again.
  const isReadOnly =
    !!property?.archivedAt ||
    (isGroupProperty &&
      !!pagePropertyId &&
      pagePropertyId !== String(propertyId));
  const { units: groupUnits } = useGroupUnits(
    isGroupProperty && propertyId != null ? String(propertyId) : undefined,
  );
  const statusPropertyIds = useMemo(() => {
    const ids = new Set<number>();
    if (propertyId != null && !Number.isNaN(propertyId)) ids.add(propertyId);
    for (const u of groupUnits) {
      const n = Number(u.id);
      if (!Number.isNaN(n)) ids.add(n);
    }
    return Array.from(ids);
  }, [propertyId, groupUnits]);
  const statusMaps = useCostPaymentStatusMaps(
    statusInvoices,
    statusPropertyIds,
  );
  const invoice = rawInvoice
    ? withLivePaidSplits(
        rawInvoice,
        statusMaps.byOrderId,
        statusMaps.byRequestId,
      )
    : undefined;

  // Is this cost already part of a confirmed afrekening? Then the drawer's
  // per-tenant status reads "Opgenomen in afrekening" (the settlement tracks the
  // payment now), matching the costs overview. Settlements live on the cost's
  // property, and for a unit cost also on its parent group. A group cost can
  // also sit in the departure settlements of the group's units.
  const parentGroupId = (property as any)?.parentId as number | undefined;
  const { items: propSettlements } = useSettlements({
    unitId:
      propertyId != null && !Number.isNaN(propertyId)
        ? String(propertyId)
        : undefined,
    unitIds: groupUnits.map(unit => String(unit.id)),
  });
  const { items: groupSettlements } = useSettlements({
    groupId: parentGroupId != null ? String(parentGroupId) : undefined,
  });
  // Siblings of a legacy pre-split group cost (same `groupCostRef`) — deleting
  // one child would leave the others behind, so the drawer removes them all.
  const { items: siblings } = useSiblingInvoices(rawInvoice?.groupCostRef);
  // Opened from the wizard's summary: how that settlement splits the cost.
  // The wizard behind the drawer holds a draft, so the cost is only read
  // there: its "Wijzigen" is picked up by the cost list alone, and a cost
  // the draft holds is not deleted from under it.
  const settlementSplit = useCostDrawerSplit(propertyCostId);
  const canChange = canEdit && !isReadOnly && !settlementSplit;
  // A departure settlement settled one tenant's share and leaves the rest
  // open: such a cost reads "Deels afgerekend (vertrek)" and stays editable.
  const settledInfo = useMemo(
    () =>
      costSettledInfo(
        costSettlementStateOf(
          costSettlementStates([...propSettlements, ...groupSettlements]),
          propertyCostId,
        ),
      ),
    [propSettlements, groupSettlements, propertyCostId],
  );

  // A link to a cost that is gone or not the viewer's says so instead of
  // opening an empty drawer (X05).
  if (!invoice) {
    return (
      <MissingDetail
        kind="cost"
        state={getMissingDetailState({ isFetching, fetchError })}
        onClose={onDrawerClose}
        onRetry={refetch}
      />
    );
  }

  // Its address with the box spelled out, "Scenariolaan 1 bus 1, 9000 Gent"
  // (unitAddressLabel), as the summary and the cost choice write it.
  const propertyLabel =
    property?.name ??
    (property
      ? unitAddressLabel({
          code: formatAddress(property as any, false),
          box: (property as any).box?.trim() || undefined,
          addressParts: addressPartsOf(property as any),
        })
      : undefined);
  const propertyType: "unit" | "building" | undefined = (() => {
    if (!property) return undefined;
    return (property as any).typeId === EPropertyTypeId.Group
      ? "building"
      : "unit";
  })();

  const onDelete = () => {
    // For a pre-split group cost every unit holds its own child record; the
    // group list shows them as one row, so all of them have to go together.
    const targetIds =
      siblings.length > 0
        ? [...new Set([propertyCostId, ...siblings.map(s => s.id)])]
        : [propertyCostId];
    const ownRows = invoice ? [invoice] : [];
    const targets = siblings.length > 0 ? siblings : ownRows;
    // Same rule as the cost lists: an OPEN payment invitation or order is
    // tenant-facing, so deleting is blocked until the broker withdraws that
    // payment under Betalingen. Paid splits don't block — nothing is open to
    // withdraw, and the payment history stays. Judged by the live payment
    // status; the server has the last word (deleteRefusedMessage below).
    const isGroupCost = targets.some(target => target.scope === "common");
    if (costsHaveOpenPayment({ invoices: targets, statusMaps })) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: openPaymentMessage({ kind: "cost", isGroup: isGroupCost }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    const doDelete = async () => {
      try {
        // The refusal is explained below; no generic toast next to it.
        await Promise.all(
          targetIds.map(id =>
            propertyCostPromises.remove(dispatch, {
              id,
              suppressFailureAlert: true,
            }),
          ),
        );
        onDrawerClose?.();
      } catch (error) {
        // Chained after the confirm dialog that is still closing.
        setTimeout(() =>
          confirm({
            type: "warning",
            title: getLocalizedText("costs.delete.title"),
            info: deleteRefusedMessage(error, "cost", {
              isGroup: isGroupCost,
            }),
            secondaryAction: {
              title: getLocalizedText("payment_request.button.ok"),
            },
          }),
        );
      }
    };
    // Product rule (Lewis, 2026-08-30): a cost inside a confirmed settlement
    // cannot be deleted — the settlement's frozen totals would diverge from
    // its cost list. Delete the settlement first, then the cost. Together
    // with the edit lock this makes a confirmed settlement untouchable.
    if (settledInfo.settled) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_settled"),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    // A departure settlement froze a tenant's share of the cost: it goes
    // first, the cost after it.
    const [departure] = settledInfo.departures ?? [];
    if (departure) {
      confirm({
        type: "warning",
        title: getLocalizedText("costs.delete.title"),
        info: getLocalizedText("costs.delete.blocked_departure", {
          name: departure.settlementName,
        }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    confirm({
      title: getLocalizedText("costs.delete.title"),
      info: getLocalizedText("costs.delete.confirm", {
        category: invoice ? CATEGORY_LABELS_NL[invoice.category] : "",
        supplier: invoice?.supplier ?? "",
      }),
      primaryActions: [
        {
          title: getLocalizedText("costs.delete.title"),
          onPress: doDelete,
        },
      ],
    });
  };

  const onEdit = () => {
    // Hand off to the parent's edit modal via a separate query param so the
    // existing InvoiceModal (with full property + assignable wiring) opens.
    setEditParams({
      propertyCostEditId: propertyCostId,
      propertyCostId: undefined,
    });
  };

  return (
    <InvoiceViewModal
      invoice={invoice}
      propertyLabel={propertyLabel}
      propertyType={propertyType}
      settledInfo={settledInfo}
      splitContext={settlementSplit?.splitContext}
      settlementName={settlementSplit?.settlementName}
      openedFor={settlementSplit?.openedFor}
      onEdit={canChange ? onEdit : undefined}
      onDelete={canChange ? onDelete : undefined}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default PropertyCost;
